import { useState, useEffect, useCallback } from 'react'
import { useAuth } from './useAuth'
import { useOrganization } from '../contexts/OrganizationContext'
import { getOrgLegalPendientes } from '../lib/supabase'
import { decidirPuertaLegal } from '../lib/legalGateDecision'

// Cuanto se tolera no saber el rol antes de dar la espera por perdida. El reloj vive
// aqui y no en la decision: la funcion pura recibe el dato, no lo mide.
const ESPERA_ROL_MS = 8000

// Determina si la org del usuario debe pasar por la puerta legal (aceptar DPA +
// Contrato) antes de usar la app. No aplica a platform_admin (no firma por el club).
export const useOrgLegalGate = () => {
  const { user, staff, isPlatformAdmin } = useAuth()
  const { org, loading: orgLoading } = useOrganization()
  const [loading, setLoading] = useState(true)
  const [pendientes, setPendientes] = useState([])
  const [yaActivada, setYaActivada] = useState(false)
  const [puedeFirmar, setPuedeFirmar] = useState(false)

  const orgId = org?.id
  const applies = !!user && !isPlatformAdmin && !!orgId

  const load = useCallback(async () => {
    if (!applies) {
      setPendientes([])
      setLoading(false)
      return
    }
    setLoading(true)
    const { pendientes: pend, yaActivada: activada, puedeFirmar: firma, error } = await getOrgLegalPendientes(orgId)
    // Si la consulta FALLA no se limpia lo que ya se sabia: vaciar `pendientes` ante
    // un error de red apagaria la puerta y dejaria pasar sin aceptar nada.
    if (!error) {
      setPendientes(pend || [])
      setYaActivada(!!activada)
      setPuedeFirmar(!!firma)
    }
    setLoading(false)
  }, [applies, orgId])

  useEffect(() => { load() }, [load])

  // Solo se acota la espera del rol: hay sesion y organizacion, pero `staff` no llega.
  // Es la unica espera que puede no terminar nunca (useAuth no reintenta ni degrada).
  const [esperaAgotada, setEsperaAgotada] = useState(false)
  const esperandoRol = applies && !orgLoading && !staff

  useEffect(() => {
    if (!esperandoRol) {
      setEsperaAgotada(false)
      return
    }
    const t = setTimeout(() => setEsperaAgotada(true), ESPERA_ROL_MS)
    return () => clearTimeout(t)
  }, [esperandoRol])

  const decision = decidirPuertaLegal({
    user,
    isPlatformAdmin,
    orgLoading,
    orgId,
    staff,
    pendientes,
    yaActivada,
    puedeFirmar,
    cargandoPendientes: loading,
    esperaAgotada,
  })

  return {
    // La app DEBE mirar esto: mientras se espera, muestra el splash en vez de pintar
    // contenido que un instante despues va a tapar. Antes se devolvia y nadie lo leia,
    // asi que "esperando" acababa comportandose como "pasa".
    loading: decision.estado === 'esperando',
    gated: decision.estado === 'bloquea',
    esActualizacion: yaActivada,
    pendientes,
    isAdmin: decision.puedeAceptar,
    orgName: org?.name || '',
    orgId,
    refetch: load,
  }
}

export default useOrgLegalGate
